import * as pdfjsLib from 'pdfjs-dist';
import type { CatalogueItem } from '../util/ndis-catalogue';
import { extractPlanModel } from '../util/ndis-plan-model';
import { groupCatalogueByCategory, parseCatalogueRate, readCatalogueFromStorage, searchCatalogueItems } from '../util/ndis-catalogue-search';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();

interface ParticipantBudgetItem {
  category: string;
  amount: number;
  managementType: string;
  sourceSnippet: string;
}

interface ParticipantPlanContext {
  planStartDate: string;
  planEndDate: string;
  planManagementType: 'self' | 'plan-managed' | 'agency' | 'unknown';
  budgets: ParticipantBudgetItem[];
  participantNameFromPlan: string;
  ndisNumberFromPlan: string;
  warnings: string[];
  sourceType: 'pdf-upload' | 'pasted-text' | 'manual';
  sourceFileName: string;
  extractedAt: string;
}

interface ParticipantApplicableCode {
  supportItemCode: string;
  supportItemName: string;
  category: string;
  registrationGroup: string;
  unit: string;
  claimType: string;
  priceLimit: number;
  source: 'catalogue-matched' | 'manual';
  notes: string;
  reviewStatus: 'needs-review' | 'approved' | 'inactive';
  selectedAt: string;
}

interface ParticipantProfileDraft {
  name: string;
  ndisNumber: string;
  billingType: 'plan-managed' | 'self-managed' | 'ndia-managed' | 'unknown';
  planManagerName: string;
  planManagerEmail: string;
  phone: string;
  address: string;
  invoiceNotes: string;
  preferredInvoiceRecipient: string;
  planContext?: ParticipantPlanContext;
  applicableCodes: ParticipantApplicableCode[];
}

type PdfParseStatus = 'idle' | 'extracting' | 'done' | 'error';

const PARTICIPANT_STORAGE_KEY = 'hub-participant-draft';
const CATALOGUE_STORAGE_KEY = 'hub-ndis-catalogue';
const PRIVACY_TEXT = 'To request deletion or modification of your submitted profile information, email accounts@the-hub-ecosystems.com. Where possible, send the request from the same email address used when submitting the information. If that email was entered incorrectly, is unavailable, or may be slightly different, please explain this in your request. Additional verification may be required before records are changed or deleted.';
const PLAN_WARNING = 'Plan budgets and funding categories are not the same as claimable invoice line items. Support item codes must be selected and reviewed separately.';
const TESTING_WARNING = 'This is testing only. Do not use extracted or matched codes for invoicing until reviewed and confirmed.';
const CODE_WARNING = 'Support item codes and rates require review before use in invoices.';

let participantDraft: ParticipantProfileDraft = defaultParticipantDraft();
let selectedPdfFile: File | null = null;
let pdfParseStatus: PdfParseStatus = 'idle';
let pdfParseMessage = 'Status: idle';
let pastedPlanText = '';
let catalogueSearch = '';
let expandedCatalogueCategories = new Set<string>();
let manualCodeDraft = {
  supportItemCode: '',
  supportItemName: '',
  category: '',
  unit: '',
  priceLimit: '',
};

function defaultPlanContext(): ParticipantPlanContext {
  return {
    planStartDate: '',
    planEndDate: '',
    planManagementType: 'unknown',
    budgets: [],
    participantNameFromPlan: '',
    ndisNumberFromPlan: '',
    warnings: [],
    sourceType: 'manual',
    sourceFileName: '',
    extractedAt: '',
  };
}

function defaultParticipantDraft(): ParticipantProfileDraft {
  return {
    name: '',
    ndisNumber: '',
    billingType: 'unknown',
    planManagerName: '',
    planManagerEmail: '',
    phone: '',
    address: '',
    invoiceNotes: '',
    preferredInvoiceRecipient: '',
    applicableCodes: [],
  };
}

function demoParticipantDraft(): ParticipantProfileDraft {
  return {
    name: 'Alex Demo Participant',
    ndisNumber: 'DEMO-1234567',
    billingType: 'plan-managed',
    planManagerName: 'Demo Plan Manager',
    planManagerEmail: 'billing@example.com',
    phone: '0400 111 222',
    address: '10 Demo Street, Sydney NSW 2000',
    invoiceNotes: 'Demo only. Confirm all billing details before use.',
    preferredInvoiceRecipient: 'Demo Plan Manager billing team',
    planContext: {
      ...defaultPlanContext(),
      planStartDate: '2026-01-01',
      planEndDate: '2026-12-31',
      planManagementType: 'plan-managed',
      budgets: [{ category: 'Core supports', amount: 12000, managementType: 'plan-managed', sourceSnippet: 'Demo budget row' }],
      participantNameFromPlan: 'Alex Demo Participant',
      ndisNumberFromPlan: 'DEMO-1234567',
      sourceType: 'manual',
      extractedAt: new Date().toISOString(),
      warnings: [PLAN_WARNING],
    },
    applicableCodes: [],
  };
}

function injectStyles(): void {
  if (document.getElementById('participant-profile-builder-styles')) return;
  const style = document.createElement('style');
  style.id = 'participant-profile-builder-styles';
  style.textContent = `
body.sp-public-body { display: block !important; min-height: 100dvh; height: auto !important; overflow-y: auto !important; background: var(--bg) !important; }
.sp-public-root { display: block !important; width: 100% !important; min-height: 100dvh !important; height: auto !important; overflow: visible !important; flex: none !important; background: var(--bg); }
.ppb-page { min-height: 100dvh; width: 100%; box-sizing: border-box; background: var(--bg); color: var(--fg); font-family: var(--font-sans); padding: 24px; }
.ppb-shell { width: 100%; max-width: 1180px; margin: 0 auto; }
.ppb-topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px solid var(--border); }
.ppb-brand { color: var(--fg-muted); font-size: 12px; font-weight: 700; text-transform: uppercase; }
.ppb-header h1 { margin: 6px 0 6px; color: var(--fg); font-size: 24px; line-height: 1.2; font-weight: 800; }
.ppb-header p { margin: 0; color: var(--fg-muted); font-size: 14px; line-height: 1.45; }
.ppb-badges { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.ppb-badge { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 4px; border: 1px solid var(--warning); background: var(--bg-secondary); color: var(--warning); font-size: 11px; font-weight: 700; text-transform: uppercase; }
.ppb-warning-stack { display: grid; gap: 8px; margin-bottom: 16px; }
.ppb-warning { padding: 12px 14px; border: 1px solid var(--warning); border-radius: 8px; background: var(--bg-secondary); color: var(--fg); font-size: 13px; line-height: 1.45; font-weight: 700; }
.ppb-card { border: 1px solid var(--border); border-radius: 8px; background: var(--bg-secondary); padding: 14px; margin-bottom: 14px; }
.ppb-card h2 { margin: 0 0 12px; color: var(--fg); font-size: 17px; line-height: 1.25; }
.ppb-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.ppb-field { min-width: 0; }
.ppb-field-wide { grid-column: 1 / -1; }
.ppb-label { display: block; margin-bottom: 5px; color: var(--fg-muted); font-size: 12px; font-weight: 700; }
.ppb-input, .ppb-select, .ppb-textarea { width: 100%; min-height: 38px; box-sizing: border-box; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); padding: 8px 10px; font: inherit; font-size: 13px; }
.ppb-textarea { min-height: 94px; resize: vertical; }
.ppb-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
.ppb-actions-left { justify-content: flex-start; }
.ppb-button { min-height: 38px; border: 1px solid var(--accent); border-radius: 6px; background: var(--accent); color: var(--bg); padding: 8px 14px; font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; text-decoration: none; display: inline-flex; align-items: center; justify-content: center; }
.ppb-button-secondary { border-color: var(--border); background: var(--bg); color: var(--fg); }
.ppb-button-danger { border-color: var(--danger); background: var(--bg); color: var(--danger); }
.ppb-button:disabled { border-color: var(--border); background: var(--bg-tertiary); color: var(--fg-muted); cursor: not-allowed; }
.ppb-file-input { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.ppb-notice, .ppb-status { margin-top: 12px; padding: 12px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); font-size: 13px; line-height: 1.55; }
.ppb-status-error { border-color: var(--danger); color: var(--danger); }
.ppb-status-done { border-color: var(--success); color: var(--success); }
.ppb-table-wrap { overflow-x: auto; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); margin-top: 12px; }
.ppb-table { width: 100%; min-width: 760px; border-collapse: collapse; font-size: 12px; }
.ppb-table th, .ppb-table td { padding: 8px; border-bottom: 1px solid var(--border); text-align: left; vertical-align: top; }
.ppb-table th { color: var(--fg-muted); background: var(--bg-tertiary); text-transform: uppercase; font-size: 11px; }
.ppb-table .ppb-input, .ppb-table .ppb-select, .ppb-table .ppb-textarea { min-height: 34px; padding: 6px 8px; font-size: 12px; }
.ppb-search-panel { display: grid; gap: 12px; }
.ppb-cat-list { display: grid; gap: 8px; margin-top: 12px; }
.ppb-cat-group { margin-top: 8px; padding: 7px 9px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg-tertiary); color: var(--fg); font-size: 12px; font-weight: 800; }
.ppb-cat-row { display: grid; grid-template-columns: minmax(150px, 0.4fr) minmax(0, 1fr) minmax(120px, auto) auto; gap: 10px; align-items: center; padding: 9px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); font-size: 12px; }
.ppb-cat-code { font-family: monospace; color: var(--accent); font-weight: 800; }
.ppb-cat-price { text-align: right; font-weight: 800; white-space: nowrap; }
.ppb-cat-category { border: 1px solid var(--border); border-radius: 8px; background: var(--bg); margin-top: 10px; overflow: hidden; }
.ppb-cat-category-head { width: 100%; min-height: 42px; border: none; border-bottom: 1px solid var(--border); background: var(--bg-tertiary); color: var(--fg); padding: 9px 12px; display: flex; justify-content: space-between; gap: 12px; font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; text-align: left; }
.ppb-cat-category-items { display: grid; gap: 8px; padding: 10px 10px 10px 24px; }
.ppb-code-list { display: grid; gap: 10px; margin-top: 12px; }
.ppb-code-card { border: 1px solid var(--border); border-radius: 8px; background: var(--surface-1); padding: 12px; }
.ppb-code-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; margin-bottom: 10px; }
.ppb-code-title { font-weight: 800; color: var(--fg); }
.ppb-code-meta { color: var(--fg-muted); font-size: 12px; line-height: 1.45; }
.ppb-chip { display: inline-flex; min-height: 22px; align-items: center; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-tertiary); color: var(--fg-muted); padding: 2px 7px; font-size: 11px; font-weight: 800; }
@media (max-width: 768px) {
  .ppb-page { padding: 16px; }
  .ppb-topbar { flex-direction: column; }
  .ppb-badges { justify-content: flex-start; }
  .ppb-fields, .ppb-cat-row { grid-template-columns: 1fr; }
  .ppb-actions { justify-content: stretch; }
  .ppb-button { width: 100%; }
  .ppb-cat-price { text-align: left; }
}
`;
  document.head.appendChild(style);
}

function shell(): HTMLElement {
  document.title = 'Participant Profile Builder - Hub People';
  injectStyles();
  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';
  for (const id of ['sidebar-scrim', 'modal-overlay', 'toast']) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  const page = document.createElement('div');
  page.className = 'ppb-page';
  root.appendChild(page);
  return page;
}

function esc(value: string): string {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}

function money(value: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number.isFinite(value) ? value : 0);
}

function normalizePlanManagement(value: string): ParticipantPlanContext['planManagementType'] {
  const lower = value.toLowerCase();
  if (lower.includes('self')) return 'self';
  if (lower.includes('plan')) return 'plan-managed';
  if (lower.includes('agency') || lower.includes('ndia')) return 'agency';
  return 'unknown';
}

function parseAmount(value: string): number {
  const parsed = Number(value.replace(/[$,\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeDraft(parsed: Partial<ParticipantProfileDraft>): ParticipantProfileDraft {
  return {
    ...defaultParticipantDraft(),
    ...parsed,
    applicableCodes: Array.isArray(parsed.applicableCodes) ? parsed.applicableCodes : [],
    planContext: parsed.planContext
      ? { ...defaultPlanContext(), ...parsed.planContext, budgets: Array.isArray(parsed.planContext.budgets) ? parsed.planContext.budgets : [] }
      : undefined,
  };
}

function readDraft(): ParticipantProfileDraft {
  try {
    const raw = localStorage.getItem(PARTICIPANT_STORAGE_KEY);
    if (!raw) return defaultParticipantDraft();
    return normalizeDraft(JSON.parse(raw) as Partial<ParticipantProfileDraft>);
  } catch {
    return defaultParticipantDraft();
  }
}

function saveDraft(): void {
  localStorage.setItem(PARTICIPANT_STORAGE_KEY, JSON.stringify(participantDraft));
}

function inputField(field: keyof ParticipantProfileDraft, label: string, wide = false): string {
  return `
    <label class="ppb-field${wide ? ' ppb-field-wide' : ''}">
      <span class="ppb-label">${esc(label)}</span>
      <input class="ppb-input" data-field="${esc(field)}" type="text" value="${esc(String(participantDraft[field] ?? ''))}">
    </label>
  `;
}

function textareaField(field: keyof ParticipantProfileDraft, label: string): string {
  return `
    <label class="ppb-field ppb-field-wide">
      <span class="ppb-label">${esc(label)}</span>
      <textarea class="ppb-textarea" data-field="${esc(field)}">${esc(String(participantDraft[field] ?? ''))}</textarea>
    </label>
  `;
}

function billingTypeField(): string {
  const options: ParticipantProfileDraft['billingType'][] = ['unknown', 'plan-managed', 'self-managed', 'ndia-managed'];
  return `
    <label class="ppb-field">
      <span class="ppb-label">Billing type</span>
      <select class="ppb-select" data-field="billingType">
        ${options.map(option => `<option value="${esc(option)}"${participantDraft.billingType === option ? ' selected' : ''}>${esc(option)}</option>`).join('')}
      </select>
    </label>
  `;
}

function ensurePlanContext(): ParticipantPlanContext {
  if (!participantDraft.planContext) participantDraft.planContext = defaultPlanContext();
  return participantDraft.planContext;
}

function planInput(field: keyof ParticipantPlanContext, label: string, type = 'text'): string {
  const context = participantDraft.planContext ?? defaultPlanContext();
  return `
    <label class="ppb-field">
      <span class="ppb-label">${esc(label)}</span>
      <input class="ppb-input" data-plan-field="${esc(field)}" type="${esc(type)}" value="${esc(String(context[field] ?? ''))}">
    </label>
  `;
}

function planManagementField(): string {
  const context = participantDraft.planContext ?? defaultPlanContext();
  const options: ParticipantPlanContext['planManagementType'][] = ['unknown', 'self', 'plan-managed', 'agency'];
  return `
    <label class="ppb-field">
      <span class="ppb-label">Management type</span>
      <select class="ppb-select" data-plan-field="planManagementType">
        ${options.map(option => `<option value="${esc(option)}"${context.planManagementType === option ? ' selected' : ''}>${esc(option)}</option>`).join('')}
      </select>
    </label>
  `;
}

function renderBudgetRows(): string {
  const budgets = participantDraft.planContext?.budgets ?? [];
  if (budgets.length === 0) return '<div class="ppb-notice">No budget rows saved yet. Add rows manually or extract plan text.</div>';
  return `
    <div class="ppb-table-wrap">
      <table class="ppb-table">
        <thead><tr><th>Category</th><th>Amount</th><th>Management</th><th>Source snippet</th><th></th></tr></thead>
        <tbody>${budgets.map((budget, index) => `
          <tr data-budget-index="${index}">
            <td><input class="ppb-input" data-budget-field="category" value="${esc(budget.category)}"></td>
            <td><input class="ppb-input" data-budget-field="amount" type="number" min="0" step="0.01" value="${budget.amount}"></td>
            <td><input class="ppb-input" data-budget-field="managementType" value="${esc(budget.managementType)}"></td>
            <td><textarea class="ppb-textarea" data-budget-field="sourceSnippet">${esc(budget.sourceSnippet)}</textarea></td>
            <td><button class="ppb-button ppb-button-danger" data-remove-budget="${index}" type="button">Remove</button></td>
          </tr>
        `).join('')}</tbody>
      </table>
    </div>
  `;
}

function renderPlanContextSection(): string {
  const context = participantDraft.planContext ?? defaultPlanContext();
  return `
    <section class="ppb-card">
      <h2>Plan Context</h2>
      <div class="ppb-warning">${esc(PLAN_WARNING)}</div>
      <input id="ppb-plan-pdf" class="ppb-file-input" type="file" accept="application/pdf,.pdf">
      <div class="ppb-actions ppb-actions-left">
        <label class="ppb-button" for="ppb-plan-pdf">Upload NDIS Plan</label>
        <button class="ppb-button ppb-button-secondary" id="ppb-save-plan-context" type="button">Save plan context to profile</button>
      </div>
      <div class="ppb-status ppb-status-${esc(pdfParseStatus)}">${esc(selectedPdfFile ? `${selectedPdfFile.name} - ${pdfParseMessage}` : pdfParseMessage)}</div>
      <label class="ppb-field ppb-field-wide">
        <span class="ppb-label">Paste plan text fallback</span>
        <textarea class="ppb-textarea" id="ppb-plan-paste" rows="6">${esc(pastedPlanText)}</textarea>
      </label>
      <div class="ppb-actions ppb-actions-left">
        <button class="ppb-button ppb-button-secondary" id="ppb-extract-paste" type="button">Extract pasted plan text</button>
      </div>
      <div class="ppb-fields">
        ${planInput('planStartDate', 'Plan start date', 'date')}
        ${planInput('planEndDate', 'Plan end date', 'date')}
        ${planManagementField()}
        ${planInput('participantNameFromPlan', 'Participant name from plan')}
        ${planInput('ndisNumberFromPlan', 'NDIS number from plan')}
        <div class="ppb-field"><span class="ppb-label">Source</span><div class="ppb-notice">${esc(context.sourceType)} ${context.sourceFileName ? `- ${esc(context.sourceFileName)}` : ''}${context.extractedAt ? `<br>Extracted ${esc(context.extractedAt)}` : ''}</div></div>
      </div>
      <h2>Budget rows</h2>
      ${renderBudgetRows()}
      <div class="ppb-actions ppb-actions-left">
        <button class="ppb-button ppb-button-secondary" id="ppb-add-budget" type="button">Add budget row</button>
      </div>
      ${context.warnings.length > 0 ? `<div class="ppb-notice">${context.warnings.map(esc).join('<br>')}</div>` : ''}
    </section>
  `;
}

function applicableCodeFromCatalogue(item: CatalogueItem): ParticipantApplicableCode {
  return {
    supportItemCode: item.supportItemNumber,
    supportItemName: item.supportItemName,
    category: item.supportCategory,
    registrationGroup: item.registrationGroup,
    unit: item.unit,
    claimType: 'standard',
    priceLimit: parseCatalogueRate(item.priceLimit),
    source: 'catalogue-matched',
    notes: '',
    reviewStatus: 'needs-review',
    selectedAt: new Date().toISOString(),
  };
}

function renderCatalogueRow(item: CatalogueItem): string {
  return `
    <div class="ppb-cat-row">
      <div class="ppb-cat-code">${esc(item.supportItemNumber)}</div>
      <div>
        <strong>${esc(item.supportItemName)}</strong>
        <div class="ppb-code-meta">${esc(item.supportCategory)} ${esc(item.registrationGroup)}</div>
      </div>
      <div class="ppb-cat-price">${money(parseCatalogueRate(item.priceLimit))}/${esc(item.unit || 'unit')}</div>
      <button class="ppb-button ppb-button-secondary" type="button" data-select-code="${esc(item.supportItemNumber)}">Select</button>
    </div>
  `;
}

function renderCatalogueSearch(): string {
  const catalogue = readCatalogueFromStorage(CATALOGUE_STORAGE_KEY);
  if (catalogue.length === 0) {
    return '<div class="ppb-warning">Upload official NDIS Support Catalogue XLSX on the NDIS Extraction page to search real support item codes.</div>';
  }
  const matches = searchCatalogueItems(catalogue, catalogueSearch, 50);
  const allMatches = searchCatalogueItems(catalogue, catalogueSearch);
  const groupedMatches = groupCatalogueByCategory(matches);
  const groupedCatalogue = groupCatalogueByCategory(catalogue);
  return `
    <div class="ppb-search-panel">
      <label class="ppb-field ppb-field-wide">
        <span class="ppb-label">Search code, name, category, or registration group</span>
        <input class="ppb-input" id="ppb-catalogue-search" type="search" value="${esc(catalogueSearch)}">
      </label>
      <div class="ppb-code-meta">${allMatches.length} results. Showing ${matches.length}.</div>
      <div class="ppb-cat-list">
        ${groupedMatches.map(group => `<div class="ppb-cat-group">${esc(group.category)} (${group.items.length})</div>${group.items.map(renderCatalogueRow).join('')}`).join('')}
      </div>
      <h2>Browse by category</h2>
      <div class="ppb-cat-list">${groupedCatalogue.map(group => {
        const expanded = expandedCatalogueCategories.has(group.category);
        return `
          <section class="ppb-cat-category">
            <button class="ppb-cat-category-head" type="button" data-toggle-category="${esc(group.category)}">
              <span>${esc(group.category)}</span><span>${group.items.length} items</span>
            </button>
            ${expanded ? `<div class="ppb-cat-category-items">${group.items.slice(0, 80).map(renderCatalogueRow).join('')}</div>` : ''}
          </section>
        `;
      }).join('')}</div>
    </div>
  `;
}

function renderSelectedCodes(): string {
  if (participantDraft.applicableCodes.length === 0) return '<div class="ppb-notice">No applicable line codes selected.</div>';
  const statuses: ParticipantApplicableCode['reviewStatus'][] = ['needs-review', 'approved', 'inactive'];
  return `
    <div class="ppb-code-list">
      ${participantDraft.applicableCodes.map((code, index) => `
        <article class="ppb-code-card" data-code-index="${index}">
          <div class="ppb-code-head">
            <div>
              <div class="ppb-code-title">${esc(code.supportItemCode)} - ${esc(code.supportItemName)}</div>
              <div class="ppb-code-meta">${esc(code.category)} ${esc(code.registrationGroup)} - ${money(code.priceLimit)}/${esc(code.unit || 'unit')}</div>
            </div>
            <span class="ppb-chip">${esc(code.source)} / ${esc(code.reviewStatus)}</span>
          </div>
          <div class="ppb-fields">
            <label class="ppb-field">
              <span class="ppb-label">Status</span>
              <select class="ppb-select" data-code-field="reviewStatus">
                ${statuses.map(status => `<option value="${esc(status)}"${status === code.reviewStatus ? ' selected' : ''}>${esc(status)}</option>`).join('')}
              </select>
            </label>
            <label class="ppb-field">
              <span class="ppb-label">Claim type</span>
              <input class="ppb-input" data-code-field="claimType" value="${esc(code.claimType)}">
            </label>
            <label class="ppb-field ppb-field-wide">
              <span class="ppb-label">Notes</span>
              <textarea class="ppb-textarea" data-code-field="notes">${esc(code.notes)}</textarea>
            </label>
          </div>
          <div class="ppb-actions">
            <button class="ppb-button ppb-button-danger" type="button" data-remove-code="${index}">Remove</button>
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

function renderManualCodeEntry(): string {
  return `
    <h2>Manual code entry</h2>
    <div class="ppb-fields">
      <label class="ppb-field"><span class="ppb-label">Code</span><input class="ppb-input" data-manual-code-field="supportItemCode" value="${esc(manualCodeDraft.supportItemCode)}"></label>
      <label class="ppb-field"><span class="ppb-label">Name</span><input class="ppb-input" data-manual-code-field="supportItemName" value="${esc(manualCodeDraft.supportItemName)}"></label>
      <label class="ppb-field"><span class="ppb-label">Category</span><input class="ppb-input" data-manual-code-field="category" value="${esc(manualCodeDraft.category)}"></label>
      <label class="ppb-field"><span class="ppb-label">Unit</span><input class="ppb-input" data-manual-code-field="unit" value="${esc(manualCodeDraft.unit)}"></label>
      <label class="ppb-field"><span class="ppb-label">Rate</span><input class="ppb-input" data-manual-code-field="priceLimit" type="number" min="0" step="0.01" value="${esc(manualCodeDraft.priceLimit)}"></label>
    </div>
    <div class="ppb-actions ppb-actions-left">
      <button class="ppb-button ppb-button-secondary" id="ppb-add-manual-code" type="button">Add manual code</button>
    </div>
  `;
}

function renderApplicableCodesSection(): string {
  return `
    <section class="ppb-card">
      <h2>Applicable Line Codes</h2>
      <div class="ppb-warning">${esc(CODE_WARNING)}</div>
      <div class="ppb-warning">Support item codes and rates require review. Do not use for invoicing until confirmed.</div>
      ${renderCatalogueSearch()}
      <h2>Selected codes</h2>
      ${renderSelectedCodes()}
      ${renderManualCodeEntry()}
    </section>
  `;
}

function renderContent(page: HTMLElement): void {
  page.innerHTML = `
    <main class="ppb-shell">
      <div class="ppb-topbar">
        <header class="ppb-header">
          <div class="ppb-brand">the hub ecosystems</div>
          <h1>Participant Profile Builder</h1>
          <p>Internal test profile draft for NDIS-aware invoicing.</p>
        </header>
        <div class="ppb-badges">
          <span class="ppb-badge">Auth-gated</span>
          <span class="ppb-badge">Local only</span>
          <span class="ppb-badge">Test</span>
        </div>
      </div>
      <div class="ppb-warning-stack">
        <div class="ppb-warning">Test only. No production data stored. No real participant profile created.</div>
        <div class="ppb-warning">${esc(TESTING_WARNING)}</div>
      </div>
      <section class="ppb-card">
        <h2>Billing and Contact</h2>
        <div class="ppb-fields">
          ${inputField('name', 'Name')}
          ${inputField('ndisNumber', 'NDIS number/reference')}
          ${billingTypeField()}
          ${inputField('planManagerName', 'Plan manager/billing contact name')}
          ${inputField('planManagerEmail', 'Plan manager email')}
          ${inputField('phone', 'Phone (optional)')}
          ${inputField('address', 'Address (optional)', true)}
          ${textareaField('invoiceNotes', 'Invoice notes')}
          ${inputField('preferredInvoiceRecipient', 'Preferred invoice recipient', true)}
        </div>
        <div class="ppb-actions">
          <button class="ppb-button ppb-button-secondary" id="ppb-load-demo" type="button">Load demo</button>
          <button class="ppb-button" id="ppb-save" type="button">Save local draft</button>
        </div>
      </section>
      ${renderPlanContextSection()}
      ${renderApplicableCodesSection()}
      <div class="ppb-notice">${esc(PRIVACY_TEXT)}</div>
    </main>
  `;
  wireEvents(page);
}

function syncField(field: keyof ParticipantProfileDraft, value: string): void {
  if (field === 'billingType') {
    const allowed: ParticipantProfileDraft['billingType'][] = ['plan-managed', 'self-managed', 'ndia-managed', 'unknown'];
    participantDraft.billingType = allowed.includes(value as ParticipantProfileDraft['billingType']) ? value as ParticipantProfileDraft['billingType'] : 'unknown';
    return;
  }
  if (field === 'applicableCodes' || field === 'planContext') return;
  participantDraft = { ...participantDraft, [field]: value };
}

function applyPlanText(text: string, sourceType: ParticipantPlanContext['sourceType'], sourceFileName: string): void {
  const model = extractPlanModel(text);
  participantDraft.planContext = {
    planStartDate: model.metadata.planStartDate,
    planEndDate: model.metadata.planEndDate,
    planManagementType: normalizePlanManagement(model.metadata.managementType),
    budgets: model.budgets.map((budget) => ({
      category: budget.categoryName,
      amount: parseAmount(budget.amount),
      managementType: budget.managementType,
      sourceSnippet: budget.sourceSnippet,
    })),
    participantNameFromPlan: model.metadata.participantName,
    ndisNumberFromPlan: model.metadata.ndisNumber,
    warnings: [...model.metadata.warnings, PLAN_WARNING],
    sourceType,
    sourceFileName,
    extractedAt: new Date().toISOString(),
  };
  saveDraft();
}

function textContentItemToString(item: object): string {
  if ('str' in item && typeof item.str === 'string') return item.str;
  return '';
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
        return;
      }
      reject(new Error('PDF file could not be read.'));
    });
    reader.addEventListener('error', () => reject(new Error('PDF file could not be read.')));
    reader.readAsArrayBuffer(file);
  });
}

async function extractTextFromPdf(file: File): Promise<string> {
  const data = await readFileAsArrayBuffer(file);
  const pdf = await pdfjsLib.getDocument({ data }).promise;
  const pageTexts: string[] = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    pageTexts.push(content.items.map(textContentItemToString).filter(Boolean).join(' '));
  }
  return pageTexts.join('\n\n').trim();
}

function addManualCode(): void {
  if (!manualCodeDraft.supportItemCode.trim() || !manualCodeDraft.supportItemName.trim()) return;
  participantDraft.applicableCodes = [
    ...participantDraft.applicableCodes,
    {
      supportItemCode: manualCodeDraft.supportItemCode.trim(),
      supportItemName: manualCodeDraft.supportItemName.trim(),
      category: manualCodeDraft.category.trim(),
      registrationGroup: '',
      unit: manualCodeDraft.unit.trim(),
      claimType: 'standard',
      priceLimit: parseAmount(manualCodeDraft.priceLimit),
      source: 'manual',
      notes: '',
      reviewStatus: 'needs-review',
      selectedAt: new Date().toISOString(),
    },
  ];
  manualCodeDraft = { supportItemCode: '', supportItemName: '', category: '', unit: '', priceLimit: '' };
  saveDraft();
}

function wireEvents(page: HTMLElement): void {
  page.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-field]').forEach(input => {
    const handle = (): void => {
      const field = input.dataset['field'] as keyof ParticipantProfileDraft | undefined;
      if (!field) return;
      syncField(field, input.value);
      saveDraft();
    };
    input.addEventListener('input', handle);
    input.addEventListener('change', handle);
  });

  page.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-plan-field]').forEach(input => {
    const handle = (): void => {
      const field = input.dataset['planField'] as keyof ParticipantPlanContext | undefined;
      if (!field) return;
      const context = ensurePlanContext();
      if (field === 'planManagementType') context.planManagementType = normalizePlanManagement(input.value);
      else if (field !== 'budgets' && field !== 'warnings') context[field] = input.value as never;
      context.sourceType = context.sourceType || 'manual';
      if (!context.extractedAt) context.extractedAt = new Date().toISOString();
      saveDraft();
    };
    input.addEventListener('input', handle);
    input.addEventListener('change', handle);
  });

  page.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-budget-field]').forEach(input => {
    const handle = (): void => {
      const row = input.closest<HTMLElement>('[data-budget-index]');
      const index = Number(row?.dataset['budgetIndex']);
      const field = input.dataset['budgetField'] as keyof ParticipantBudgetItem | undefined;
      const budget = participantDraft.planContext?.budgets[index];
      if (!budget || !field) return;
      if (field === 'amount') budget.amount = parseAmount(input.value);
      else budget[field] = input.value as never;
      saveDraft();
    };
    input.addEventListener('input', handle);
    input.addEventListener('change', handle);
  });

  page.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-code-field]').forEach(input => {
    const handle = (): void => {
      const card = input.closest<HTMLElement>('[data-code-index]');
      const index = Number(card?.dataset['codeIndex']);
      const field = input.dataset['codeField'] as keyof ParticipantApplicableCode | undefined;
      const code = participantDraft.applicableCodes[index];
      if (!code || !field) return;
      if (field === 'reviewStatus') code.reviewStatus = input.value as ParticipantApplicableCode['reviewStatus'];
      else if (field === 'notes' || field === 'claimType') code[field] = input.value;
      saveDraft();
    };
    input.addEventListener('input', handle);
    input.addEventListener('change', handle);
  });

  page.querySelectorAll<HTMLInputElement>('[data-manual-code-field]').forEach(input => {
    input.addEventListener('input', () => {
      const field = input.dataset['manualCodeField'] as keyof typeof manualCodeDraft | undefined;
      if (!field) return;
      manualCodeDraft = { ...manualCodeDraft, [field]: input.value };
    });
  });

  page.querySelector<HTMLInputElement>('#ppb-plan-pdf')?.addEventListener('change', event => {
    const input = event.target as HTMLInputElement;
    selectedPdfFile = input.files?.item(0) ?? null;
    if (!selectedPdfFile) return;
    pdfParseStatus = 'extracting';
    pdfParseMessage = 'Status: extracting';
    renderContent(page);
    void extractTextFromPdf(selectedPdfFile)
      .then((text) => {
        pastedPlanText = text;
        if (text.trim().length === 0) {
          pdfParseStatus = 'error';
          pdfParseMessage = 'Status: no readable text found. Paste plan text below.';
          return;
        }
        applyPlanText(text, 'pdf-upload', selectedPdfFile?.name ?? '');
        pdfParseStatus = 'done';
        pdfParseMessage = 'Status: extracted and saved';
      })
      .catch(() => {
        pdfParseStatus = 'error';
        pdfParseMessage = 'Status: error reading PDF. Paste plan text below.';
      })
      .finally(() => renderContent(page));
  });

  page.querySelector<HTMLTextAreaElement>('#ppb-plan-paste')?.addEventListener('input', event => {
    pastedPlanText = (event.target as HTMLTextAreaElement).value;
  });

  page.querySelector<HTMLButtonElement>('#ppb-extract-paste')?.addEventListener('click', () => {
    applyPlanText(pastedPlanText, 'pasted-text', '');
    pdfParseStatus = 'done';
    pdfParseMessage = 'Status: pasted text extracted and saved';
    renderContent(page);
  });

  page.querySelector<HTMLButtonElement>('#ppb-add-budget')?.addEventListener('click', () => {
    const context = ensurePlanContext();
    context.sourceType = 'manual';
    context.extractedAt = context.extractedAt || new Date().toISOString();
    context.budgets.push({ category: '', amount: 0, managementType: '', sourceSnippet: '' });
    saveDraft();
    renderContent(page);
  });

  page.querySelectorAll<HTMLButtonElement>('[data-remove-budget]').forEach(button => {
    button.addEventListener('click', () => {
      const index = Number(button.dataset['removeBudget']);
      const context = ensurePlanContext();
      context.budgets = context.budgets.filter((_, budgetIndex) => budgetIndex !== index);
      saveDraft();
      renderContent(page);
    });
  });

  page.querySelector<HTMLInputElement>('#ppb-catalogue-search')?.addEventListener('input', event => {
    catalogueSearch = (event.target as HTMLInputElement).value;
    renderContent(page);
  });

  page.querySelectorAll<HTMLButtonElement>('[data-toggle-category]').forEach(button => {
    button.addEventListener('click', () => {
      const category = button.dataset['toggleCategory'];
      if (!category) return;
      if (expandedCatalogueCategories.has(category)) expandedCatalogueCategories.delete(category);
      else expandedCatalogueCategories.add(category);
      renderContent(page);
    });
  });

  page.querySelectorAll<HTMLButtonElement>('[data-select-code]').forEach(button => {
    button.addEventListener('click', () => {
      const code = button.dataset['selectCode'] ?? '';
      const item = readCatalogueFromStorage(CATALOGUE_STORAGE_KEY).find(catalogueItem => catalogueItem.supportItemNumber === code);
      if (!item) return;
      participantDraft.applicableCodes = [
        ...participantDraft.applicableCodes.filter(existing => existing.supportItemCode !== item.supportItemNumber),
        applicableCodeFromCatalogue(item),
      ];
      saveDraft();
      renderContent(page);
    });
  });

  page.querySelectorAll<HTMLButtonElement>('[data-remove-code]').forEach(button => {
    button.addEventListener('click', () => {
      const index = Number(button.dataset['removeCode']);
      participantDraft.applicableCodes = participantDraft.applicableCodes.filter((_, codeIndex) => codeIndex !== index);
      saveDraft();
      renderContent(page);
    });
  });

  page.querySelector<HTMLButtonElement>('#ppb-add-manual-code')?.addEventListener('click', () => {
    addManualCode();
    renderContent(page);
  });

  page.querySelector<HTMLButtonElement>('#ppb-save-plan-context')?.addEventListener('click', () => {
    const context = ensurePlanContext();
    context.sourceType = context.sourceType || 'manual';
    context.extractedAt = context.extractedAt || new Date().toISOString();
    saveDraft();
    renderContent(page);
  });

  page.querySelector<HTMLButtonElement>('#ppb-load-demo')?.addEventListener('click', () => {
    participantDraft = demoParticipantDraft();
    saveDraft();
    renderContent(page);
  });
  page.querySelector<HTMLButtonElement>('#ppb-save')?.addEventListener('click', () => {
    saveDraft();
    renderContent(page);
  });
}

export function isParticipantProfileBuilderPath(): boolean {
  return window.location.pathname.includes('participant-profile-builder');
}

export function renderParticipantProfileBuilder(): void {
  participantDraft = readDraft();
  renderContent(shell());
}
